import React, { useEffect, useMemo, useState } from "react";
import { Button, InputNumber, Select, Typography, message } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import useAuth from "hooks/useAuth";
import instance from "api/cmsapi";
import { getCommentsBySpecificTable, invalidateCommentSummaryQueries } from "service/comment.service";
import { useQueryClient } from "react-query";
import { useDpatCommentFeedback } from "context/DpatCommentFeedbackContext";
import {
  CI_SCORE_OPTIONS,
  MANUAL_SCORE_FIELDS,
  clampNumericManualScore,
  findScoreAnchorComment,
  findSummaryRowForSlug,
  formatManualScoreDisplay,
  getManualScoreFieldForRole,
  indicatorKindFromSlug,
  mergeManualScoreDddpData,
  parseManualScoresFromComments,
  resolveMaxScore,
  resolveSystemScoreDisplay,
  slugFromTableCommentedId,
  validateManualScoreValue,
} from "utils/dpatManualScoreHelpers";


const { Text } = Typography;

const ROLE_LABELS = {
  [MANUAL_SCORE_FIELDS.TECHNICAL_TEAM]: "Technical Team",
  [MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE]: "Quality Assurance",
  [MANUAL_SCORE_FIELDS.PETITION_COMMITTEE]: "Petition Committee",
};

/** After a role completes their stage, their score field becomes read-only. */
function isManualScoreFieldOpen(fieldKey, assessmentWorkflowStatus) {
  if (fieldKey === MANUAL_SCORE_FIELDS.TECHNICAL_TEAM) {
    return assessmentWorkflowStatus === "Start";
  }
  if (fieldKey === MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE) {
    return assessmentWorkflowStatus === "Pending";
  }
  if (fieldKey === MANUAL_SCORE_FIELDS.PETITION_COMMITTEE) {
    return assessmentWorkflowStatus === "Completed";
  }
  return false;
}

function ManualScoreInput({ indicatorKind, value, maxScore, disabled, onChange }) {
  if (indicatorKind === "CI") {
    return (
      <Select
        allowClear
        placeholder="Agree with system"
        value={value || undefined}
        disabled={disabled}
        onChange={(next) => onChange(next ?? "")}
        options={CI_SCORE_OPTIONS}
        style={{ width: "100%" }}
      />
    );
  }

  const numericValue = value === "" || value == null ? null : Number(value);

  return (
    <InputNumber
      min={0}
      max={maxScore ?? undefined}
      step={1}
      precision={0}
      placeholder={maxScore != null ? `Agree with system (0 – ${maxScore})` : "Agree with system"}
      value={Number.isFinite(numericValue) ? numericValue : null}
      disabled={disabled}
      style={{ width: "100%" }}
      onChange={(next) => {
        if (next == null || next === "") {
          onChange("");
          return;
        }
        const clamped = clampNumericManualScore(next, maxScore);
        if (clamped.exceedsMax) {
          message.warning(`Score cannot exceed the maximum score (${maxScore})`);
        }
        onChange(clamped.value);
      }}
    />
  );
}

export default function DpatManualScorePanel({
  districtId,
  year,
  tableCommentedId,
  comments,
  data,
  manualScoreConfig,
  hideComment,
  onCommentsUpdated,
}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const {
    scoreSheetSummary,
    assessmentStartTables,
    getIndicatorMaxScore,
    assessmentWorkflowStatus,
  } = useDpatCommentFeedback();
  const roleNames = user?.user?.userRoles?.map((role) => role.name) ?? [];
  const editableField = getManualScoreFieldForRole(roleNames);
  const [draftScores, setDraftScores] = useState({
    technicalTeamScore: "",
    qualityAssuranceScore: "",
    petitionCommitteeScore: "",
  });
  const [savingField, setSavingField] = useState(null);

  const slug = slugFromTableCommentedId(tableCommentedId);
  const indicatorKind =
    manualScoreConfig?.indicatorType || indicatorKindFromSlug(slug);
  const summaryRow = useMemo(
    () => findSummaryRowForSlug(scoreSheetSummary, slug),
    [scoreSheetSummary, slug],
  );
  const systemScoreDisplay = useMemo(
    () =>
      resolveSystemScoreDisplay({
        indicatorKind,
        summaryRow,
        manualScoreConfig,
        scoreSheetSummary,
        assessmentStartTables,
        slug,
      }),
    [
      indicatorKind,
      summaryRow,
      manualScoreConfig,
      scoreSheetSummary,
      assessmentStartTables,
      slug,
    ],
  );
  const maxScore = resolveMaxScore({
    indicatorKind,
    summaryRow,
    manualScoreConfig,
    assessmentStartTables,
    slug,
    data,
    comments,
    registeredMaxScore: getIndicatorMaxScore(slug),
  });

  useEffect(() => {
    const parsed = parseManualScoresFromComments(comments);
    if (indicatorKind !== "CI" && maxScore != null) {
      Object.keys(parsed).forEach((key) => {
        if (parsed[key]) {
          parsed[key] = clampNumericManualScore(parsed[key], maxScore).value;
        }
      });
    }
    setDraftScores(parsed);
  }, [comments, indicatorKind, maxScore]);

  if (hideComment || !editableField || !indicatorKind) {
    return null;
  }

  const saveScore = async (fieldKey) => {
    const roleCanEdit = editableField === "ALL" || editableField === fieldKey;
    if (!roleCanEdit || !isManualScoreFieldOpen(fieldKey, assessmentWorkflowStatus)) {
      message.warning("This score can no longer be edited for the current assessment stage");
      return;
    }
    const validation = validateManualScoreValue({
      indicatorKind,
      value: draftScores[fieldKey],
      maxScore,
    });
    if (!validation.valid) {
      message.error(validation.message);
      return;
    }
    if (!districtId || year == null) {
      message.error("District or year is missing");
      return;
    }

    setSavingField(fieldKey);
    try {
      const anchor = findScoreAnchorComment(comments);
      const commentDate = new Date().toISOString().split("T")[0].split("-").map(Number);
      const dddpData = mergeManualScoreDddpData(
        anchor,
        tableCommentedId,
        data,
        fieldKey,
        validation.normalized,
        comments,
      );
      // New score rows must use the same role convention as comment creates,
      // otherwise QA/Petition POSTs can return success without persisting dddpData scores.
      const payload = {
        id: anchor?.id || 0,
        username: anchor?.username || user?.user?.username,
        fullName: anchor?.fullName || user?.user?.fullName,
        userRole: anchor?.userRole || "DPAT_TECHNICAL_TEAM",
        type: "DPAT",
        districtId,
        year: Number.isFinite(Number(year)) ? Number(year) : year,
        tableCommented: tableCommentedId,
        comments: anchor
          ? (anchor.comments != null && anchor.comments !== "" ? anchor.comments : anchor.comment) ?? ""
          : "",
        gaps: anchor?.gaps || "",
        commentDate: anchor?.commentDate || commentDate,
        updateDate: commentDate,
        dddpDataDate: commentDate,
        dddpData,
      };

      let savedRow = null;
      if (anchor?.id) {
        const response = await instance.put(`comments/${anchor.id}`, payload);
        savedRow = response?.data ?? { ...anchor, ...payload, id: anchor.id, dddpData };
      } else {
        const response = await instance.post("comments", payload);
        savedRow = response?.data ?? { ...payload, id: response?.data?.id || payload.id || Date.now(), dddpData };
      }

      let nextComments = comments;
      if (typeof onCommentsUpdated === "function") {
        try {
          const yearForApi = Number.isFinite(Number(year)) ? Number(year) : year;
          const refreshed = await queryClient.fetchQuery(
            ["getCommentsBySpecificTable", districtId, yearForApi, "DPAT", tableCommentedId],
            () => getCommentsBySpecificTable(districtId, yearForApi, "DPAT", tableCommentedId),
          );
          nextComments = Array.isArray(refreshed?.data)
            ? refreshed.data
            : Array.isArray(refreshed)
              ? refreshed
              : [];
        } catch {
          nextComments = [];
        }

        const persisted = parseManualScoresFromComments(nextComments)[fieldKey];
        const expected = String(validation.normalized || "").trim();
        if (String(persisted || "").trim() !== expected) {
          // Keep the saved score visible even if the table GET is momentarily stale.
          if (anchor?.id) {
            nextComments = (nextComments.length ? nextComments : comments).map((comment) =>
              comment.id === anchor.id ? { ...comment, ...savedRow, dddpData } : comment,
            );
          } else if (savedRow) {
            const existing = nextComments.length ? nextComments : comments;
            const already = existing.some((comment) => comment.id && comment.id === savedRow.id);
            nextComments = already
              ? existing.map((comment) =>
                  comment.id === savedRow.id ? { ...comment, ...savedRow, dddpData } : comment,
                )
              : [...existing, { ...savedRow, dddpData }];
          }
        }

        onCommentsUpdated(nextComments);
      }

      invalidateCommentSummaryQueries(queryClient);
      message.success(`${ROLE_LABELS[fieldKey]} score saved`);
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to save manual score");
    } finally {
      setSavingField(null);
    }
  };

  const fields = [
    MANUAL_SCORE_FIELDS.TECHNICAL_TEAM,
    MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE,
    MANUAL_SCORE_FIELDS.PETITION_COMMITTEE,
  ];

  return (
    <div
      className="dpat-manual-score-panel"
      style={{
        marginTop: 16,
        marginBottom: 8,
        padding: "14px 16px",
        borderRadius: 10,
        border: "1px solid #dbe4f0",
        background: "#f8fafc",
      }}
    >
      <Text strong style={{ display: "block", marginBottom: 4, fontSize: 13 }}>
        Manual score
      </Text>
      <Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 12 }}>
        Leave blank to agree with the previous score. Final score uses Petition, then Quality Assurance, then Technical Team, then the system score. System score:{" "}
        <strong>{systemScoreDisplay}</strong>
        {indicatorKind !== "CI" && maxScore != null ? (
          <>
            {" "}
            (maximum allowed: <strong>{maxScore}</strong>)
          </>
        ) : null}
      </Text>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
        }}
      >
        {/* System Score - Standalone, Read-only Field */}
        <div>
          <Text strong style={{ display: "block", marginBottom: 6, fontSize: 12 }}>
            System score
          </Text>
          <Text style={{ display: "block", minHeight: 32, lineHeight: "32px" }}>
            {systemScoreDisplay}
          </Text>
        </div>

        {fields.map((fieldKey) => {
          const canEdit =
            (editableField === "ALL" || editableField === fieldKey) &&
            isManualScoreFieldOpen(fieldKey, assessmentWorkflowStatus);
          const storedValue = parseManualScoresFromComments(comments)[fieldKey];
          const hasDraftChange = draftScores[fieldKey] !== storedValue;
          const draftValidation = validateManualScoreValue({
            indicatorKind,
            value: draftScores[fieldKey],
            maxScore,
          });

          return (
            <div key={fieldKey}>
              <Text strong style={{ display: "block", marginBottom: 6, fontSize: 12 }}>
                {ROLE_LABELS[fieldKey]}
              </Text>
              {canEdit ? (
                <>
                  <ManualScoreInput
                    indicatorKind={indicatorKind}
                    value={draftScores[fieldKey]}
                    maxScore={maxScore}
                    disabled={savingField === fieldKey}
                    onChange={(value) =>
                      setDraftScores((prev) => ({ ...prev, [fieldKey]: value ?? "" }))
                    }
                  />
                  {!draftValidation.valid ? (
                    <Text type="danger" style={{ display: "block", marginTop: 6, fontSize: 12 }}>
                      {draftValidation.message}
                    </Text>
                  ) : null}
                  {draftValidation.valid && hasDraftChange && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<SaveOutlined />}
                      loading={savingField === fieldKey}
                      onClick={() => saveScore(fieldKey)}
                      style={{ marginTop: 8, backgroundColor: "#16325a", borderColor: "#16325a" }}
                    >
                      Save
                    </Button>
                  )}
                </>
              ) : (
                <Text style={{ display: "block", minHeight: 32, lineHeight: "32px" }}>
                  {formatManualScoreDisplay(storedValue)}
                </Text>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
